import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AzureManagementGroup,
  AzureSubscription,
  OnboardingAzureResourceGroup,
  CloudAccountType,
  type AzureOnboardingStatus,
  type AzureTenant,
  AzureOnboardingStatusStatusEnum,
} from '@repo/api-gw-sdk';
import React, { useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useUser } from '@/contexts/useUser';

import CopyableCode from '../shared/copyableCode';

enum AzureDeploymentMethod {
  Interactive = 'Interactive',
  Bash = 'Bash',
  BashNew = 'BashNew',
  Terraform = 'Terraform',
}

enum TerraformDeploymentScope {
  ManagementGroup = 'ManagementGroup',
  Subscription = 'Subscription',
}

enum BashNewScope {
  ManagementGroup = 'ManagementGroup',
  Subscription = 'Subscription',
}

interface CreateAzureAccountPanelProps {
  accountType: CloudAccountType;
  onCreated: (success: boolean, sourceAccountId: string) => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
}

export default function CreateAzureAccountPanel(
  props: CreateAzureAccountPanelProps
) {
  const user = useUser();
  const dal = useDAL();
  const {
    terraformAzureOnboarding,
    ownCustomerOnboardingEnabled,
    consolidateOnboardingExperienceEnabled,
  } = useFeatureFlags();

  const requestId = useRef(uuid());

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<
    string | undefined
  >();
  const [tenants, setTenants] = useState<AzureTenant[] | undefined>();
  const [managementGroups, setManagementGroups] = useState<
    AzureManagementGroup[] | null
  >();
  const [subscriptions, setSubscriptions] = useState<
    AzureSubscription[] | null
  >();
  const [selectedEntity, setSelectedEntity] = useState<
    AzureManagementGroup | AzureSubscription | null
  >(null);
  const [onboardingStatus, setOnboardingStatus] = useState<
    AzureOnboardingStatus[] | null
  >(null);
  const [deploymentMethod, setDeploymentMethod] =
    useState<AzureDeploymentMethod>(
      ownCustomerOnboardingEnabled
        ? AzureDeploymentMethod.Terraform
        : AzureDeploymentMethod.Interactive
    );

  const [terraformDeploymentScope, setTerraformDeploymentScope] =
    useState<TerraformDeploymentScope>(TerraformDeploymentScope.Subscription);

  const [terraformEntityId, setTerraformEntityId] = useState<string | null>(
    null
  );
  const [terraformTenantId, setTerraformTenantId] = useState<string | null>(
    null
  );
  const [terraformRestoreAppId, setTerraformRestoreAppId] = useState<
    string | null
  >(null);
  const [terraformLocation, setTerraformLocation] = useState<string | null>(
    'eastus2'
  );

  const [bashNewTenantId, setBashNewTenantId] = useState<string>('');
  const [bashNewSubscriptionId, setBashNewSubscriptionId] =
    useState<string>('');
  const [bashNewManagementGroupId, setBashNewManagementGroupId] =
    useState<string>('');
  const [bashNewScope, setBashNewScope] = useState<BashNewScope>(
    BashNewScope.Subscription
  );

  const [state, setState] = useState<'initial' | 'pending' | 'completed'>(
    'initial'
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: connectIdRes } = dal.azure.getAccountConnectId();

  const { data: scanningAppIdRes } = dal.azure.getApplicationId();

  const { data: backupAppsRes } = dal.onboarding.applications.get('Source');
  // here all conect pannel...
  /**
   * Switch directory, then *pass the new tenant ID* into openConsentDialog
   * so that openConsentDialog is guaranteed to use the updated ID.
   */
  const switchDirectory = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setManagementGroups(null);
    setSubscriptions(null);
    setTenants(undefined);
    setSelectedEntity(null);
    setOnboardingStatus(null);
    setErrorMessage(null);

    // Pass the new tenant ID directly
    openConsentDialog(tenantId);
  };

  /**
   * Updated to accept an optional tenantIdParam.
   * If provided, use that; otherwise fall back to the selectedTenantId state.
   */
  const openConsentDialog = (tenantIdParam?: string) => {
    const tid = tenantIdParam ?? selectedTenantId;
    const width = 600,
      height = 800;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    props.setLoading(true);
    setErrorMessage(null);
    setManagementGroups(null);
    setSubscriptions(null);
    setSelectedEntity(null);
    setOnboardingStatus(null);

    void dal.azure.getConsentUrl(requestId.current, tid).then((resp) => {
      if (resp.error) {
        props.setLoading(false);
        setErrorMessage(resp.error);
        return;
      }

      const authWindow = window.open(
        resp.url,
        'authWindow',
        `width=${width},height=${height},top=${top},left=${left}`
      );

      // Monitor the popup window
      const interval = setInterval(() => {
        try {
          if (!authWindow) {
            clearInterval(interval);
            props.setLoading(false);
            return;
          }

          // Close if user closes the window
          if (authWindow.closed) {
            clearInterval(interval);
            void dal.azure
              .listSubscriptions(requestId.current)
              .then((resp) => {
                if (resp.managementGroups?.length > 0) {
                  const firstSubTenantId = resp.managementGroups
                    .filter(
                      (mg) => mg.subscriptions && mg.subscriptions.length > 0
                    )
                    .map((mg) => mg.subscriptions?.[0]?.tenantId)
                    .find((tenantId) => tenantId !== undefined);

                  setSelectedTenantId(firstSubTenantId);
                } else if (
                  resp.subscriptions &&
                  resp.subscriptions?.length > 0
                ) {
                  setSelectedTenantId(resp.subscriptions[0].tenantId);
                }

                setManagementGroups(resp.managementGroups);
                setSubscriptions(resp.subscriptions);
                setTenants(resp.tenants);
              })
              .catch(() => {
                props.setLoading(false);
                setErrorMessage('Operation failed');
                setSubscriptions(null);
                setManagementGroups(null);
              });
          }
        } catch {
          // Ignore cross-origin errors
        }
      }, 500);
    });
  };

  const connectAccountTerraform = () => {
    if (!terraformEntityId || !terraformTenantId) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    if (
      props.accountType === CloudAccountType.Restore &&
      (!terraformRestoreAppId || !terraformLocation)
    ) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    props.setLoading(true);
    setErrorMessage(null);

    const subId =
      terraformDeploymentScope === TerraformDeploymentScope.Subscription
        ? terraformEntityId
        : undefined;
    const mgId =
      terraformDeploymentScope === TerraformDeploymentScope.ManagementGroup
        ? terraformEntityId
        : undefined;

    dal.azure
      .connectAccountTerraform(
        props.accountType,
        terraformTenantId,
        subId,
        mgId,
        terraformRestoreAppId || '',
        terraformLocation || ''
      )
      .then((resp) => {
        props.setLoading(false);
        if (resp.status === AzureOnboardingStatusStatusEnum.Succeeded) {
          props.onCreated(true, resp.sourceAccountId || '');
          setState('completed');
        } else {
          setErrorMessage(`Failed to connect account - ${resp.message}`);
        }
      })
      .catch((e) => {
        setErrorMessage(`Failed to connect account - ${e.message || e}`);
        props.setLoading(false);
      });
  };

  return (
    <Box className='py-[40px]'>
      {/* 1) If no management groups yet, show the initial screen */}
      {state === 'initial' && !managementGroups && !subscriptions && (
        <>
          {!props.loading && (
            <>
              <Box className='my-[15px]'>
                Before continuing, make sure you have permissions to create and
                manage a service principal and assign roles in your Azure
                subscriptions.
                <Tooltip
                  title={
                    <Box>
                      To connect your Azure subscriptions, Eon requires creating
                      a service principal and assigning specific roles.
                      <br />
                      <br />
                      In a Microsoft Entra ID directory where user setting{' '}
                      <b>Users can register applications</b> has been set to{' '}
                      <b>No</b>, you must be a member of one of the following
                      Microsoft Entra ID built-in roles (which have the action:
                      <code>
                        microsoft.directory/applications/createAsOwner
                      </code>{' '}
                      or
                      <code>microsoft.directory/applications/create</code>):
                      <br />
                      <br />
                      <ul className='list-disc ml-[24px]'>
                        <li>Global Administrator</li>
                        <li>Application Administrator</li>
                        <li>Cloud Application Administrator</li>
                        <li>Application Developer</li>
                      </ul>
                    </Box>
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: '600px',
                        padding: '16px',
                      },
                    },
                  }}
                >
                  <i className='material-symbols-help-outline-rounded h-[20px] w-[20px] p-[2px] mb-[2px] ml-[4px] align-middle cursor-default' />
                </Tooltip>
              </Box>
              <Box className='my-[15px] mb-[30px]'>
                <Stack direction='column' gap='16px'>
                  <Typography marginBottom='8px' variant='body2'>
                    Deployment method
                  </Typography>
                  <Stack
                    className={
                      !ownCustomerOnboardingEnabled
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed'
                    }
                    direction='row'
                    alignItems='center'
                    gap='12px'
                    onClick={
                      !ownCustomerOnboardingEnabled
                        ? () =>
                            setDeploymentMethod(
                              AzureDeploymentMethod.Interactive
                            )
                        : undefined
                    }
                  >
                    <Radio
                      data-testid='full-instance-radio'
                      disabled={ownCustomerOnboardingEnabled}
                      size='small'
                      checked={
                        deploymentMethod === AzureDeploymentMethod.Interactive
                      }
                    />
                    <Typography variant='body2'>Interactive</Typography>
                  </Stack>
                  <Stack
                    className={
                      !ownCustomerOnboardingEnabled
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed'
                    }
                    direction='row'
                    alignItems='center'
                    gap='12px'
                    onClick={
                      !ownCustomerOnboardingEnabled
                        ? () =>
                            setDeploymentMethod(
                              consolidateOnboardingExperienceEnabled
                                ? AzureDeploymentMethod.BashNew
                                : AzureDeploymentMethod.Bash
                            )
                        : undefined
                    }
                  >
                    <Radio
                      disabled={ownCustomerOnboardingEnabled}
                      data-testid='specific-vol-radio'
                      size='small'
                      checked={
                        consolidateOnboardingExperienceEnabled
                          ? deploymentMethod === AzureDeploymentMethod.BashNew
                          : deploymentMethod === AzureDeploymentMethod.Bash
                      }
                    />
                    <Typography variant='body2'>Bash (Azure CLI)</Typography>
                  </Stack>
                  {terraformAzureOnboarding &&
                    (props.accountType === CloudAccountType.Source ||
                      props.accountType === CloudAccountType.Restore) && (
                      <Stack
                        className='cursor-pointer'
                        direction='row'
                        alignItems='center'
                        gap='12px'
                        onClick={() =>
                          setDeploymentMethod(AzureDeploymentMethod.Terraform)
                        }
                      >
                        <Radio
                          data-testid='specific-vol-radio'
                          size='small'
                          checked={
                            deploymentMethod === AzureDeploymentMethod.Terraform
                          }
                        />
                        <Typography variant='body2'>Terraform</Typography>
                      </Stack>
                    )}
                </Stack>
              </Box>
              <Divider className='mb-[24px]' />
            </>
          )}
          {deploymentMethod === AzureDeploymentMethod.Interactive && (
            <>
              <Box className='my-[15px]'>
                After you log in, we&rsquo;ll query the Microsoft Graph to
                retrieve the Eon application principal ID, then deploy an ARM
                template to update the required RBAC roles on the selected
                permissions.
              </Box>
              <Box className='flex flex-col items-center justify-center my-[30px] gap-[8px]'>
                <Button
                  variant='contained'
                  disabled={props.loading}
                  data-testid='azure-login-btn'
                  onClick={() => openConsentDialog()}
                  sx={{ width: '400px' }}
                >
                  {props.loading && (
                    <i className='line-md-loading-loop w-[20px] h-[20px] mr-[8px]' />
                  )}
                  Open Microsoft Azure{' '}
                  <i className='material-symbols-open-in-new w-[20px] h-[20px] ml-[8px]' />
                </Button>
              </Box>
            </>
          )}
          {deploymentMethod === AzureDeploymentMethod.Bash && (
            <>
              <Box className='my-[15px]'>
                Run this command to connect your Azure management group or
                subscription:
              </Box>
              <Box
                className='flex flex-col items-center justify-center my-[30px]'
                gap='8px'
              >
                <CopyableCode
                  codeClassName='whitespace-pre-wrap'
                  code={`curl -fsSL https://eon.blob.core.windows.net/public/eon-azure.sh -o eon-azure.sh && chmod +x eon-azure.sh && ./eon-azure.sh --connect-id ${connectIdRes?.connectionId} --project-id ${user.currentProjectId} --location ${connectIdRes?.location} --account-type ${props.accountType.toLowerCase()}`}
                />
                <Button
                  variant='contained'
                  disabled={props.loading}
                  data-testid='azure-connect-complete-btn'
                  onClick={() => props.onCreated(true, '')}
                  sx={{ width: '400px', mt: '24px' }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
          {deploymentMethod === AzureDeploymentMethod.Terraform && (
            <>
              {props.accountType === CloudAccountType.Source && (
                <>
                  <Typography variant='body2' className='mt-[28px] mb-[8px]'>
                    Tenant ID
                  </Typography>
                  <TextField
                    size='small'
                    placeholder='Example: fd54b1bc-fc91-55bf-946d-aee8d476a7e7'
                    onChange={(e) => {
                      setErrorMessage(null);
                      setTerraformTenantId(e.target.value);
                    }}
                    value={terraformTenantId}
                    sx={{ width: '400px', marginBottom: '32px' }}
                  />
                  <Typography variant='body2' className='mb-[8px]'>
                    Deploy roles to
                  </Typography>
                  <Stack
                    className='cursor-pointer mt-[20px] mb-[8px]'
                    direction='row'
                    alignItems='center'
                    gap='12px'
                    onClick={() =>
                      setTerraformDeploymentScope(
                        TerraformDeploymentScope.ManagementGroup
                      )
                    }
                  >
                    <Radio
                      data-testid='specific-vol-radio'
                      size='small'
                      checked={
                        terraformDeploymentScope ===
                        TerraformDeploymentScope.ManagementGroup
                      }
                    />
                    <Typography variant='body2'>Management group</Typography>
                  </Stack>
                  <Stack
                    className='cursor-pointer'
                    direction='row'
                    alignItems='center'
                    gap='12px'
                    onClick={() =>
                      setTerraformDeploymentScope(
                        TerraformDeploymentScope.Subscription
                      )
                    }
                  >
                    <Radio
                      data-testid='specific-vol-radio'
                      size='small'
                      checked={
                        terraformDeploymentScope ===
                        TerraformDeploymentScope.Subscription
                      }
                    />
                    <Typography variant='body2'>Subscription</Typography>
                  </Stack>
                </>
              )}
              <Typography variant='body2' className='mt-[28px] mb-[8px]'>
                {terraformDeploymentScope ===
                TerraformDeploymentScope.ManagementGroup
                  ? 'Management group'
                  : 'Subscription'}{' '}
                ID
              </Typography>
              <TextField
                size='small'
                placeholder='Example: 0876fb9a-e611-5ef3-85aa-ee17cb987964'
                onChange={(e) => {
                  setErrorMessage(null);
                  setTerraformEntityId(e.target.value);
                }}
                value={terraformEntityId}
                sx={{ width: '400px' }}
              />
              {props.accountType === CloudAccountType.Restore && (
                <>
                  <Typography
                    variant='body2'
                    className='mt-[28px] mb-[8px] font-bold'
                    color='text.secondary'
                  >
                    Restore identity region
                  </Typography>
                  <TextField
                    size='small'
                    placeholder='Example: eastus2'
                    onChange={(e) => {
                      setErrorMessage(null);
                      setTerraformLocation(e.target.value);
                    }}
                    value={terraformLocation}
                    sx={{ width: '400px' }}
                  />
                </>
              )}
              <Typography
                variant='body2'
                className='mt-[28px] mb-[8px] font-bold'
                color='text.secondary'
              >
                {terraformDeploymentScope ===
                TerraformDeploymentScope.ManagementGroup
                  ? 'Management group'
                  : 'Subscription'}{' '}
                deployment
              </Typography>

              <ol className='list-decimal mx-[20px]'>
                <li className='mb-[8px]'>
                  <Typography
                    variant='body2'
                    className='mb-[8px]'
                    color='text.secondary'
                  >
                    <b>Add the Eon Terraform module to your deployment file</b>
                    <br />
                    Add this snippet to your Terraform deployment file and{' '}
                    apply.
                  </Typography>
                </li>
              </ol>
              <Box
                className='flex flex-col items-center justify-center my-[30px]'
                gap='8px'
              >
                <CopyableCode
                  code={`\
  module "azure_${props.accountType.toLowerCase()}" {
    source = "https://eon.blob.core.windows.net/public/eon-azure-${props.accountType.toLowerCase()}-tf.zip"
    ${
      props.accountType === CloudAccountType.Restore
        ? `
    project_id = "${user.currentProjectId}"
    location = "${terraformLocation ?? ''}"
    tenant_id = "${terraformTenantId ?? ''}"`
        : ''
    }
    backup_app_id = "${scanningAppIdRes?.applicationId}"
    subscription_id = "${
      terraformDeploymentScope === TerraformDeploymentScope.Subscription
        ? (terraformEntityId ?? '')
        : ''
    }"
    ${
      props.accountType === CloudAccountType.Source
        ? `management_group_id = "${
            terraformDeploymentScope ===
            TerraformDeploymentScope.ManagementGroup
              ? (terraformEntityId ?? '')
              : ''
          }"
    `
        : ''
    }
  }`}
                />
              </Box>
              {props.accountType === CloudAccountType.Source && (
                <ol start={2} className='list-decimal mx-[20px]'>
                  <li>
                    <Typography
                      variant='body2'
                      className='mb-[8px]'
                      color='text.secondary'
                    >
                      <b>Confirm the deployment</b>
                      <br />
                      After the Terraform deploymentis done, click the button{' '}
                      below to validate and connect the{' '}
                      {terraformDeploymentScope ===
                      TerraformDeploymentScope.ManagementGroup
                        ? 'management group'
                        : 'subscription'}{' '}
                      to your Eon account.
                    </Typography>
                  </li>
                </ol>
              )}
              {props.accountType === CloudAccountType.Restore && (
                <>
                  <ol className='list-decimal mx-[20px]' start={2}>
                    <li className='mb-[8px]'>
                      <b>Confirm the deployment</b>
                      <br />
                      In your Azure tenant, find the Eon restore application{' '}
                      created by the Terraform module, named{' '}
                      <code>
                        eon-restore-app-{user.currentProjectId.substring(0, 8)}
                      </code>
                      . Paste the application ID in the field below.
                    </li>
                  </ol>
                  <Typography
                    variant='body2'
                    className='mt-[28px] mb-[8px] font-bold'
                    color='text.secondary'
                  >
                    Restore application ID
                  </Typography>
                  <TextField
                    size='small'
                    placeholder='Example: 4dba2fc3-8f5e-5b11-9f12-613c715b3d8f'
                    onChange={(e) => {
                      setErrorMessage(null);
                      setTerraformRestoreAppId(e.target.value);
                    }}
                    value={terraformRestoreAppId}
                    sx={{ width: '400px' }}
                  />
                </>
              )}
              <Box
                className='flex flex-col items-center justify-center mb-[8px]'
                gap='8px'
              >
                <Button
                  variant='contained'
                  disabled={props.loading}
                  data-testid='azure-connect-complete-btn'
                  onClick={connectAccountTerraform}
                  sx={{ width: '400px', mt: '24px' }}
                >
                  Connect{' '}
                  {terraformDeploymentScope ===
                  TerraformDeploymentScope.ManagementGroup
                    ? 'Management Group'
                    : 'Subscription'}
                </Button>
              </Box>
            </>
          )}
          {deploymentMethod === AzureDeploymentMethod.BashNew && (
            <>
              {/* Step 1: Run the script */}
              <Box className='my-[15px]'>
                <Typography variant='h6' className='mb-[8px]'>
                  1. Run the onboarding script
                </Typography>
                <Typography variant='body2'>
                  Enter your Azure tenant and{' '}
                  {bashNewScope === BashNewScope.Subscription
                    ? 'subscription'
                    : 'management group'}{' '}
                  details, then run the generated command in your terminal.
                </Typography>
              </Box>

              {/* Input Fields */}
              <Box className='my-[15px]'>
                <Typography variant='body2' className='mb-[8px]'>
                  Tenant ID
                </Typography>
                <TextField
                  size='small'
                  placeholder='Example: fd54b1bc-fc91-55bf-946d-aee8d476a7e7'
                  value={bashNewTenantId}
                  onChange={(e) => {
                    setErrorMessage(null);
                    setBashNewTenantId(e.target.value);
                  }}
                  sx={{ width: '400px', marginBottom: '24px' }}
                />

                {/* Scope Selection */}
                {props.accountType === CloudAccountType.Source && (
                  <>
                    <Typography variant='body2' className='mb-[8px]'>
                      Deploy roles to
                    </Typography>
                    <Stack
                      className='cursor-pointer mt-[8px] mb-[8px]'
                      direction='row'
                      alignItems='center'
                      gap='12px'
                      onClick={() => setBashNewScope(BashNewScope.Subscription)}
                    >
                      <Radio
                        data-testid='bash-tzachi-subscription-radio'
                        size='small'
                        checked={bashNewScope === BashNewScope.Subscription}
                      />
                      <Typography variant='body2'>Subscription</Typography>
                    </Stack>
                    <Stack
                      className='cursor-pointer mb-[24px]'
                      direction='row'
                      alignItems='center'
                      gap='12px'
                      onClick={() =>
                        setBashNewScope(BashNewScope.ManagementGroup)
                      }
                    >
                      <Radio
                        data-testid='bash-tzachi-mg-radio'
                        size='small'
                        checked={bashNewScope === BashNewScope.ManagementGroup}
                      />
                      <Typography variant='body2'>Management group</Typography>
                    </Stack>
                  </>
                )}

                {/* Conditional Input based on scope */}
                {bashNewScope === BashNewScope.Subscription ? (
                  <>
                    <Typography variant='body2' className='mb-[8px]'>
                      Subscription ID
                    </Typography>
                    <TextField
                      size='small'
                      placeholder='Example: f341b3d1-36c7-48a1-a15b-e6bcb5394d9d'
                      value={bashNewSubscriptionId}
                      onChange={(e) => {
                        setErrorMessage(null);
                        setBashNewSubscriptionId(e.target.value);
                      }}
                      sx={{ width: '400px' }}
                    />
                  </>
                ) : (
                  <>
                    <Typography variant='body2' className='mb-[8px]'>
                      Management Group ID
                    </Typography>
                    <TextField
                      size='small'
                      placeholder='Example: mg-production-001'
                      value={bashNewManagementGroupId}
                      onChange={(e) => {
                        setErrorMessage(null);
                        setBashNewManagementGroupId(e.target.value);
                      }}
                      sx={{ width: '400px' }}
                    />
                  </>
                )}
              </Box>

              {/* Validation Messages for missing backend apps */}
              {(!backupAppsRes?.managementAppId ||
                !backupAppsRes?.backupAppId) && (
                <Box className='my-[15px] p-[16px] border border-red-300 bg-red-50 rounded'>
                  <Typography variant='body2' color='error'>
                    ❌ Required Eon applications not found. Please complete Eon
                    backup application onboarding first.
                  </Typography>
                </Box>
              )}

              {/* Warning for missing user inputs */}
              {backupAppsRes?.managementAppId &&
                backupAppsRes?.backupAppId &&
                (!bashNewTenantId ||
                  (bashNewScope === BashNewScope.Subscription &&
                    !bashNewSubscriptionId) ||
                  (bashNewScope === BashNewScope.ManagementGroup &&
                    !bashNewManagementGroupId)) && (
                  <Box className='my-[15px] p-[16px] border border-orange-300 bg-orange-50 rounded'>
                    <Typography variant='body2' color='warning.main'>
                      ⚠️ Please enter both Tenant ID and{' '}
                      {bashNewScope === BashNewScope.Subscription
                        ? 'Subscription ID'
                        : 'Management Group ID'}{' '}
                      above
                    </Typography>
                  </Box>
                )}

              {/* Command display - only when ALL requirements met */}
              {backupAppsRes?.managementAppId &&
                backupAppsRes?.backupAppId &&
                bashNewTenantId &&
                ((bashNewScope === BashNewScope.Subscription &&
                  bashNewSubscriptionId) ||
                  (bashNewScope === BashNewScope.ManagementGroup &&
                    bashNewManagementGroupId)) && (
                  <>
                    <Box className='my-[15px] p-[16px] border border-blue-300 bg-blue-50 rounded'>
                      <Typography variant='body2' color='primary.main'>
                        ℹ️ This will deploy resources for tenant{' '}
                        <strong>{bashNewTenantId}</strong> and{' '}
                        {bashNewScope === BashNewScope.Subscription ? (
                          <>
                            subscription{' '}
                            <strong>{bashNewSubscriptionId}</strong>
                          </>
                        ) : (
                          <>
                            management group{' '}
                            <strong>{bashNewManagementGroupId}</strong>
                          </>
                        )}
                        . Please verify these are correct before running the
                        command.
                      </Typography>
                    </Box>

                    <Box
                      className='flex flex-col items-center justify-center my-[30px]'
                      gap='8px'
                    >
                      <CopyableCode
                        codeClassName='whitespace-pre-wrap'
                        code={
                          bashNewScope === BashNewScope.Subscription
                            ? `# Login and set Azure context in one command
az login --tenant "${bashNewTenantId}" --subscription "${bashNewSubscriptionId}"

# Get tokens in the correct context
ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

# Download and run new script
curl -fsSL https://eon.blob.core.windows.net/public/onboarding/v1.1.2/bash/source.sh -o source.sh && chmod +x source.sh && \\
AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
./source.sh \\
  --tenant-id "${bashNewTenantId}" \\
  --subscription-id "${bashNewSubscriptionId}" \\
  --management-app-id "${backupAppsRes.managementAppId}" \\
  --backup-app-id "${backupAppsRes.backupAppId}"`
                            : `# Login to tenant (no subscription context needed for management groups)
az login --tenant "${bashNewTenantId}"

# Get tokens at tenant level
ARM_TOKEN=$(az account get-access-token --query accessToken -o tsv)
GRAPH_TOKEN=$(az account get-access-token --resource https://graph.microsoft.com/ --query accessToken -o tsv)

# Download and run new script
curl -fsSL https://eon.blob.core.windows.net/public/onboarding/v1.1.2/bash/source.sh -o source.sh && chmod +x source.sh && \\
AZURE_ACCESS_TOKEN="$ARM_TOKEN" \\
AZURE_GRAPH_TOKEN="$GRAPH_TOKEN" \\
./source.sh \\
  --tenant-id "${bashNewTenantId}" \\
  --management-group-id "${bashNewManagementGroupId}" \\
  --management-app-id "${backupAppsRes.managementAppId}" \\
  --backup-app-id "${backupAppsRes.backupAppId}"`
                        }
                      />
                    </Box>

                    {/* Step 2: Connect to Eon - shown immediately.*/}
                    <Box className='my-[15px]'>
                      <Typography variant='h6' className='mb-[8px]'>
                        2. Confirm the deployment
                      </Typography>
                      <Typography variant='body2'>
                        After the script execution is complete, click the button
                        below to validate and connect the subscription to your
                        Eon account.
                      </Typography>
                    </Box>

                    {/* Success/Error messages */}
                    {successMessage && (
                      <Box className='my-[15px] p-[16px] border border-green-300 bg-green-50 rounded'>
                        <Typography variant='body2' color='success.main'>
                          ✅ {successMessage}
                        </Typography>
                      </Box>
                    )}

                    {errorMessage &&
                      deploymentMethod === AzureDeploymentMethod.BashNew && (
                        <Box className='my-[15px] p-[16px] border border-red-300 bg-red-50 rounded'>
                          <Typography variant='body2' color='error'>
                            ❌ {errorMessage}
                          </Typography>
                        </Box>
                      )}

                    {/* Connect button - following Terraform pattern */}
                    <Box className='flex flex-col items-center justify-center my-[30px]'>
                      <Button
                        variant='contained'
                        disabled={props.loading || !!successMessage}
                        data-testid='azure-bash-tzachi-connect-btn'
                        onClick={() => {
                          setErrorMessage(null);
                          setSuccessMessage(null);

                          // Use the same API call as Terraform method
                          void dal.azure
                            .connectAccountTerraform(
                              props.accountType,
                              bashNewTenantId,
                              bashNewScope === BashNewScope.Subscription
                                ? bashNewSubscriptionId
                                : undefined,
                              bashNewScope === BashNewScope.ManagementGroup
                                ? bashNewManagementGroupId
                                : undefined,
                              '', // restore app ID (only for restore accounts)
                              '' // location (only for restore accounts)
                            )
                            .then((resp) => {
                              if (
                                resp.status ===
                                AzureOnboardingStatusStatusEnum.Succeeded
                              ) {
                                setSuccessMessage(
                                  bashNewScope === BashNewScope.Subscription
                                    ? 'Subscription connected successfully!'
                                    : 'Management group connected successfully!'
                                );
                                // Call onCreated after a brief delay to show success
                                setTimeout(() => {
                                  props.onCreated(
                                    true,
                                    resp.sourceAccountId || ''
                                  );
                                }, 2000);
                              } else {
                                setErrorMessage(
                                  `Failed to connect account - ${resp.message}`
                                );
                              }
                            })
                            .catch((error) => {
                              setErrorMessage(
                                (error as Error).message ||
                                  'Failed to connect subscription'
                              );
                            });
                        }}
                        sx={{ width: '400px' }}
                      >
                        {props.loading ? 'Connecting...' : 'Connect'}
                      </Button>
                    </Box>
                  </>
                )}

              {/* Placeholder when requirements not met */}
              {!(
                backupAppsRes?.managementAppId &&
                backupAppsRes?.backupAppId &&
                bashNewTenantId &&
                ((bashNewScope === BashNewScope.Subscription &&
                  bashNewSubscriptionId) ||
                  (bashNewScope === BashNewScope.ManagementGroup &&
                    bashNewManagementGroupId))
              ) && (
                <Box className='my-[15px] p-[16px] bg-gray-100 rounded'>
                  <Typography variant='body2' color='text.secondary'>
                    📋 Command will appear here once all requirements are met
                  </Typography>
                </Box>
              )}

              {/* Close button - always shown, like other methods */}
              <Box className='flex flex-col items-center justify-center my-[30px]'>
                <Button
                  variant='text'
                  disabled={props.loading}
                  data-testid='azure-bash-tzachi-close-btn'
                  onClick={() => props.onCreated(false, '')}
                  sx={{ width: '400px' }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {/* 2) Once management groups are fetched, show the selection screen */}
      {state !== 'completed' && (
        <>
          <Box className='my-[15px]'>
            {tenants && tenants?.length > 1 && (
              <>
                <Box>
                  <Typography>Tenant</Typography>
                </Box>
                <Box className='mt-[12px] mb-[24px]'>
                  <Select
                    className='w-full'
                    displayEmpty
                    size='small'
                    value={selectedTenantId}
                    onChange={(event) => switchDirectory(event.target.value)}
                  >
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </>
            )}
            {(managementGroups || subscriptions) && (
              <EntitySelectionPanel
                accountType={props.accountType}
                managementGroups={managementGroups}
                requestId={requestId.current}
                selectedEntity={selectedEntity}
                setSelectedEntity={setSelectedEntity}
                subscriptions={subscriptions}
                state={state}
                setState={setState}
                setManagementGroups={setManagementGroups}
                setSubscriptions={setSubscriptions}
                setErrorMessage={setErrorMessage}
                setOnboardingStatus={setOnboardingStatus}
                selectedTenantId={selectedTenantId}
                setSelectedTenantId={setSelectedTenantId}
                setTerraformDeploymentScope={setTerraformDeploymentScope}
                setTerraformEntityId={setTerraformEntityId}
                deploymentMethod={deploymentMethod}
                consolidateOnboardingExperienceEnabled={
                  consolidateOnboardingExperienceEnabled
                }
              />
            )}
          </Box>
        </>
      )}

      {/* 3) Once completed, show the onboarding status screen */}
      {state === 'completed' && (
        <OnboardingCompleted
          onboardingStatus={onboardingStatus}
          onCreated={props.onCreated}
        />
      )}

      {/* 4) Error message display */}
      {errorMessage && (
        <Typography variant='body1' color='error' data-testid='error-message'>
          <Stack className='my-[15px]' direction='row' alignItems='center'>
            <i className='material-symbols-error-outline-rounded mr-[8px] text-[24px]'></i>
            {errorMessage}
          </Stack>
        </Typography>
      )}
    </Box>
  );
}

const OnboardingCompleted = ({
  onboardingStatus,
  onCreated,
}: {
  onboardingStatus: AzureOnboardingStatus[] | null;
  onCreated: (success: boolean, sourceAccountId: string) => void;
}) => {
  return (
    <Box className='my-[15px]'>
      <Box className='my-[15px]'>
        <b>Operation completed.</b>
        <Typography>Please check the status of the operation below:</Typography>
      </Box>
      <ul className='my-[15px] ml-[20px] list-disc'>
        {onboardingStatus?.map((os) => (
          <li key={os.entityId}>
            {os.entityName} - {os.status}
          </li>
        ))}
      </ul>
      <Box
        className='flex flex-col justify-center items-center my-[30px]'
        gap='8px'
      >
        <Button
          variant='contained'
          onClick={() =>
            onCreated(
              onboardingStatus?.every(
                (s) => s.status !== AzureOnboardingStatusStatusEnum.Failed
              ) || false,
              onboardingStatus?.find(
                (s) => s.status === AzureOnboardingStatusStatusEnum.Succeeded
              )?.sourceAccountId || ''
            )
          }
          sx={{ width: '400px' }}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
};

const getEntityName = (
  entity:
    | AzureManagementGroup
    | AzureSubscription
    | OnboardingAzureResourceGroup
    | null
) => {
  if (entity instanceof AzureSubscription) {
    return 'Subscription';
  }
  if (entity instanceof AzureManagementGroup) {
    return 'Management Group';
  }
  if (entity instanceof OnboardingAzureResourceGroup) {
    return 'Resource Group';
  }
  return '';
};

const EntitySelectionPanel = ({
  selectedEntity,
  setSelectedEntity,
  state,
  requestId,
  managementGroups,
  subscriptions,
  accountType,
  setState,
  setSubscriptions,
  setManagementGroups,
  setErrorMessage,
  setOnboardingStatus,
  selectedTenantId,
  deploymentMethod,
  consolidateOnboardingExperienceEnabled,
}: {
  selectedEntity: AzureManagementGroup | AzureSubscription | null;
  setSelectedEntity: (
    entity: AzureManagementGroup | AzureSubscription | null
  ) => void;
  state: 'initial' | 'pending' | 'completed';
  accountType: CloudAccountType;
  requestId: string;
  managementGroups: AzureManagementGroup[] | null | undefined;
  subscriptions: AzureSubscription[] | null | undefined;
  setSubscriptions: (subscriptions: AzureSubscription[] | null) => void;
  setManagementGroups: (
    managementGroups: AzureManagementGroup[] | null
  ) => void;
  setState: (state: 'initial' | 'pending' | 'completed') => void;
  selectedTenantId: string | undefined;
  setSelectedTenantId: (tenantId: string) => void;
  setErrorMessage: (errorMessage: string | null) => void;
  setOnboardingStatus: (onboardingStatus: AzureOnboardingStatus[]) => void;
  setTerraformDeploymentScope: (
    terraformDeploymentScope: TerraformDeploymentScope
  ) => void;
  setTerraformEntityId: (terraformEntityId: string | null) => void;
  deploymentMethod: AzureDeploymentMethod;
  consolidateOnboardingExperienceEnabled: boolean;
}) => {
  const dal = useDAL();
  const [allSubscriptions, setAllSubscriptions] = useState(true);
  const [allResourceGroups, setAllResourceGroups] = useState(true);
  const [selectedResourceGroup, setSelectedResourceGroup] =
    useState<OnboardingAzureResourceGroup | null>(null);
  const [selectedManagementGroup, setSelectedManagementGroup] =
    useState<AzureManagementGroup | null>(null);
  const [selectedSubscription, setSelectedSubscription] =
    useState<AzureSubscription | null>(null);

  const onboardSubscriptions = () => {
    setState('pending');
    if (!selectedEntity) return;
    if (!selectedTenantId) return;

    // Choose the onboarding method based on deployment method and feature flag
    const onboardingPromise =
      deploymentMethod === AzureDeploymentMethod.Interactive &&
      consolidateOnboardingExperienceEnabled
        ? dal.azure.onboardSubscriptionsInteractive(
            requestId,
            accountType,
            selectedTenantId,
            selectedEntity
          )
        : dal.azure.onboardSubscriptions(
            requestId,
            accountType,
            selectedTenantId,
            selectedEntity
          );

    void onboardingPromise
      .then(() => {
        let onboardingInProgress = false;

        const checkOnboardingStatus = async () => {
          if (onboardingInProgress) return; // Prevent overlapping calls
          onboardingInProgress = true;

          try {
            const resp = await (deploymentMethod ===
              AzureDeploymentMethod.Interactive &&
            consolidateOnboardingExperienceEnabled
              ? dal.azure.getInteractiveOnboardingStatus(requestId)
              : dal.azure.getOnboardingStatus(requestId));
            setOnboardingStatus(resp.entities);

            const allCompleted = resp.entities.every(
              (entity) =>
                entity.status !== AzureOnboardingStatusStatusEnum.Accepted &&
                entity.status !== AzureOnboardingStatusStatusEnum.Running
            );

            if (allCompleted) {
              setState('completed');
            } else {
              setTimeout(() => {
                void checkOnboardingStatus();
              }, 4000); // Retry after 4 seconds
            }
          } catch {
            setState('completed');
            setManagementGroups(null);
            setSubscriptions(null);
            setErrorMessage(
              `Failed to onboard ${getEntityName(selectedEntity)}`
            );
          } finally {
            onboardingInProgress = false; // Reset the flag after each call
          }
        };

        void checkOnboardingStatus();
      })
      .catch(() => {
        setState('completed');
        setErrorMessage(`Failed to onboard ${getEntityName(selectedEntity)}`);
      });
  };

  const mgmtGroups = useMemo(
    () => [
      ...(managementGroups || []),
      ...(subscriptions?.length
        ? [
            {
              id: 'root-subscriptions',
              name: 'Root Subscriptions',
              subscriptions,
            },
          ]
        : []),
    ],
    [managementGroups, subscriptions]
  );

  const isResourceGroupSelectionEnabled =
    accountType === CloudAccountType.Restore;

  const { data: resourceGroupsBody } = dal.azure.listSubscriptionResourceGroups(
    (isResourceGroupSelectionEnabled
      ? selectedSubscription?.subscriptionId
      : '') || '',
    requestId
  );

  return (
    <>
      <Stack gap='8px'>
        <Typography>Management group</Typography>
        <Autocomplete
          clearOnBlur
          size='small'
          value={selectedManagementGroup}
          onChange={(event, newValue) => {
            setSelectedManagementGroup(newValue);
            setAllSubscriptions(
              accountType !== CloudAccountType.Restore &&
                newValue?.id !== 'root-subscriptions'
            );
            setSelectedSubscription(null);
            setAllResourceGroups(true);
            setSelectedResourceGroup(null);
            setSelectedEntity(
              accountType !== CloudAccountType.Restore &&
                newValue?.id !== 'root-subscriptions'
                ? newValue
                : null
            );
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                sx={{
                  '& input::placeholder': {
                    color: 'var(--mui-palette-text-primary)',
                    opacity: 1,
                  },
                }}
                placeholder='Select'
                label=''
                InputLabelProps={{ shrink: true }}
              />
            );
          }}
          getOptionKey={(x) => x.id}
          getOptionLabel={(x) => x.name}
          options={mgmtGroups}
        />
        {selectedManagementGroup && (
          <>
            {accountType === CloudAccountType.Restore && (
              <Typography sx={{ marginTop: '24px' }}>Subscription</Typography>
            )}
            {accountType !== CloudAccountType.Restore && (
              <FormGroup sx={{ marginTop: '24px' }}>
                <Typography>Deploy roles to</Typography>
                <FormControlLabel
                  disabled={
                    state !== 'initial' ||
                    selectedManagementGroup.id === 'root-subscriptions'
                  }
                  control={<Radio className='mr-[4px]' />}
                  label='All subscriptions'
                  checked={allSubscriptions}
                  onChange={() => {
                    setAllSubscriptions(true);
                    setSelectedSubscription(null);
                    setSelectedResourceGroup(null);
                    setAllResourceGroups(true);
                    setSelectedEntity(selectedManagementGroup);
                  }}
                />
                <FormControlLabel
                  disabled={state !== 'initial'}
                  control={<Radio className='mr-[4px]' />}
                  label='A specific subscription'
                  checked={!allSubscriptions}
                  onChange={() => {
                    setAllSubscriptions(false);
                    setSelectedEntity(null);
                  }}
                />
              </FormGroup>
            )}
          </>
        )}
        {selectedManagementGroup && !allSubscriptions && (
          <Autocomplete
            sx={{
              marginLeft: accountType === CloudAccountType.Restore ? 0 : '40px',
            }}
            clearOnBlur
            size='small'
            value={selectedSubscription}
            onChange={(event, newValue) => {
              setSelectedSubscription(newValue);
              setAllResourceGroups(true);
              setSelectedResourceGroup(null);
              setSelectedEntity(newValue);
            }}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  sx={{
                    '& input::placeholder': {
                      color: 'var(--mui-palette-text-primary)',
                      opacity: 1,
                    },
                  }}
                  placeholder='Select'
                  label=''
                  InputLabelProps={{ shrink: true }}
                />
              );
            }}
            getOptionKey={(x) => x.id}
            getOptionLabel={(x) => x.name}
            options={selectedManagementGroup.subscriptions || []}
          />
        )}
        {isResourceGroupSelectionEnabled && selectedSubscription && (
          <FormGroup sx={{ marginTop: '24px' }}>
            <Typography>Allow Eon to restore to</Typography>
            <FormControlLabel
              disabled={state !== 'initial'}
              control={<Radio className='mr-[4px]' />}
              label='All resource groups'
              checked={allResourceGroups}
              onChange={() => {
                setAllResourceGroups(true);
                setSelectedResourceGroup(null);
                setSelectedEntity(selectedSubscription);
              }}
            />
            <FormControlLabel
              disabled={state !== 'initial'}
              control={<Radio className='mr-[4px]' />}
              label='A specific resource group'
              checked={!allResourceGroups}
              onChange={() => {
                setAllResourceGroups(false);
                setSelectedEntity(null);
              }}
            />
          </FormGroup>
        )}
        {isResourceGroupSelectionEnabled &&
          selectedSubscription &&
          !allResourceGroups && (
            <Autocomplete
              sx={{ marginLeft: '40px' }}
              clearOnBlur
              size='small'
              value={selectedResourceGroup}
              onChange={(event, newValue) => {
                setSelectedResourceGroup(newValue);
                setSelectedEntity(newValue);
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    sx={{
                      '& input::placeholder': {
                        color: 'var(--mui-palette-text-primary)',
                        opacity: 1,
                      },
                    }}
                    placeholder='Select'
                    label=''
                    InputLabelProps={{ shrink: true }}
                  />
                );
              }}
              getOptionKey={(x) => x.id}
              getOptionLabel={(x) => x.name}
              options={resourceGroupsBody?.resourceGroups || []}
            />
          )}
      </Stack>
      <Box
        className='flex flex-col justify-center items-center my-[30px]'
        gap='8px'
      >
        <Button
          variant='contained'
          // Disabled if we haven't selected any entity or if we're not in 'initial'
          disabled={state !== 'initial' || !selectedEntity}
          onClick={() => onboardSubscriptions()}
          sx={{ width: '400px' }}
        >
          {state === 'pending' && (
            <>
              <i className='line-md-loading-loop w-[20px] h-[20px] mr-[8px]' />
              Connecting your {getEntityName(selectedEntity)}...
            </>
          )}
          {state !== 'pending' && <>Connect</>}
        </Button>
      </Box>
    </>
  );
};
